export const formatMoney = (amount: number | string, currency = 'USD'): string => {
  const num = Number(amount);
  return isFinite(num)
    ? num.toLocaleString(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      })
    : '—';
};

export const formatShortId = (id?: string): string => {
  return id?.slice(-6) ?? '—';
};

export const formatDate = (date: string | Date): string => {
  if (!date) return '—';

  const finalDate = typeof date === 'string' ? date.slice(0, 10) : date.toISOString().slice(0, 10);

  const [year, month, day] = finalDate.split('-');

  return `${Number(month)}/${Number(day)}/${year}`;
};

export const formatFullAddress = (
  addr:
    | {
        fullName?: string | null;
        line1?: string | null;
        line2?: string | null;
        city?: string | null;
        state?: string | null;
        postalCode?: string | null;
        country?: string | null;
      }
    | null
    | undefined
): string | null => {
  if (!addr) return null;
  const parts = [
    addr.fullName,
    addr.line1,
    addr.line2,
    [addr.city, addr.state].filter(Boolean).join(', '),
    addr.postalCode,
    addr.country,
  ]
    .filter(Boolean)
    .join(', ');
  return parts || null;
};

/** Formats a US phone number string as (xxx) xxx-xxxx. Returns the input unchanged if not a 10-digit number. */
export const formatPhone = (value: string | null | undefined): string => {
  const digits = String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Converts a dollar-amount string to cents. Returns undefined if blank or invalid. */
export const dollarsToCents = (s: string): number | undefined => {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  if (!isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
};

/** Returns true when a dollar-string input is non-empty and not a valid non-negative number. */
export const isInvalidDollarInput = (s: string): boolean => {
  if (!s.trim()) return false;
  const n = Number(s);
  return !isFinite(n) || n < 0;
};

/** Tonnage quantities are tracked to two decimal places everywhere in the app. */
export const QUANTITY_DECIMALS = 2;

/**
 * Trims a raw numeric-input string to at most `maxDecimals` decimal places.
 *
 * Extra digits are dropped rather than rounded, so typing a third decimal is
 * ignored instead of nudging the value the user already entered. Anything the
 * input already accepts is returned untouched — including partial entries like
 * "12.", "1.10" or "-", so typing a decimal still works keystroke by keystroke.
 * Exponent notation ("1e-5") hides its decimals, so it is expanded first.
 */
export const limitDecimalPlaces = (value: string, maxDecimals = QUANTITY_DECIMALS): string => {
  const raw = value.trim();
  if (!raw) return value;

  if (/e/i.test(raw)) {
    const n = Number(raw);
    return isFinite(n) ? n.toFixed(maxDecimals) : value;
  }

  const dot = raw.indexOf('.');
  if (dot === -1) return value;
  if (raw.length - dot - 1 <= maxDecimals) return value;

  return maxDecimals === 0 ? raw.slice(0, dot) : raw.slice(0, dot + 1 + maxDecimals);
};

/**
 * Parses a quantity (tons) input into a number capped at two decimal places.
 * Returns 0 for blank or non-numeric input, matching the numeric form state.
 */
export const parseQuantityInput = (value: string): number => {
  const limited = limitDecimalPlaces(value, QUANTITY_DECIMALS);
  const n = Number(limited);
  return isFinite(n) ? n : 0;
};

/** Dollar amounts are entered to whole cents — two decimal places. */
export const MONEY_DECIMALS = 2;

/**
 * Trims a raw dollar-input string to at most two decimal places.
 *
 * Every money field in the app lands in an integer-cents column, so a third
 * decimal is rounded away by `dollarsToCents` on save. Clamping as the user
 * types makes that visible at the keystroke instead of silently changing the
 * number they entered. Keep money inputs on this rather than relying on the
 * `step="0.01"` attribute, which browsers do not enforce against typed input.
 */
export const limitMoneyInput = (value: string): string => limitDecimalPlaces(value, MONEY_DECIMALS);
